<template>
	<div class="row centered">
		<AppTable ref="balance-table"
			v-if="userStore.currBusinessId"
			:fetch-function="administrationStore.getFilteredBalanceChanges"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="currentTableTitle"
			:total-items="administrationStore.businessBalanceChangesTotal || 0"
			:items="changes"
			initial-sort-field="date"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
			:app-select-values="['10', '25', '50', '100','1000']"
			allow-download
			@export="exportChanges">
			<template #custom-header>
				<h3>{{ currentTableTitle }}</h3>
				<div class="header-actions">
					<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-plus" @click="handleAddBalance" />
					<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-download-blue" @click="toggleExportType" />
				</div>
			</template>
		</AppTable>
		<RechargeBottomSheet
			:active="showRechargeSheet"
			@close="v => closeRechargeSheet(v)"/>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import RechargeBottomSheet from "@/console-new/components/shared/RechargeBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {formatDateMMDDHHmm, formatDateOnly, formatDateTimeWithSeconds, getLocalDateFromServer} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		return {userStore, currencyStore, administrationStore, getString: languageStore.getString, languageStore, scriptStore, showNotification: notificationStore.showNotification};
	},
	name: "BalanceTable",
	emits: ["failed-fetch"],
	components: {AppButtonIcon, AppTable, RechargeBottomSheet},
	mixins: [WindowWidth],
	data() {
		return {
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'balance_logs',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "price",
					key: "amount",
					text: this.getString('variation'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "reason",
					text: this.getString('reason'),
					values: [this.getString('premium_card'),this.getString('super_update_credits'),this.getString('payment_intent_succeeded'),this.getString('issuing_reward'),this.getString('payment_recharge'),this.getString('marketing_charge'),this.getString('send_newsletter'),this.getString('marketing_topup')],
					keysArray: ["premium_card","super_update_credits","payment_intent_succeeded","issuing_reward","payment_recharge","marketing_charge","send_newsletter","marketing_topup"],
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('balance_pre_change'),
					fieldName: 'balance_pre_change',
					cols: 1,
					formatter: 'currency'
				},
				{
					text: this.getString('variation'),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
					formatter: 'currency'
				},
				{
					text: this.getString('balance_post_change'),
					fieldName: 'balance_post_change',
					cols: 1,
					formatter: 'currency'
				},
				{
					text: this.getString('reason'),
					fieldName: 'reason',
					cols: 1,
				},
			],
			limitReached: false,
			hasMore: false,
			showRechargeSheet: false,
		}
	},
	computed: {
		changes() {
			if(!this.administrationStore.businessBalanceChanges || !this.administrationStore.businessBalanceChanges.length) { return []; }
			return this.administrationStore.businessBalanceChanges.map(change => {
				change.reason = this.getString(change.reason);
				change.balance_post_change = Number(change.balance_pre_change + change.amount);
				return change;
			});
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else if (this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		currentOpenBalance() {
			let openBalance = this.userStore.currBusiness.open_balance;
			if (openBalance) {
				openBalance = this.currencyStore.dbToActualValue(openBalance);
			}
			return openBalance;
		},
		currentTableTitle() {
			return ( this.currentOpenBalance >= 0 ? this.getString('open_balance_credit',[this.currencyStore.readableCurrencyValue(this.currentOpenBalance, false)]) : this.getString('open_balance_debit',[this.currencyStore.readableCurrencyValue(this.currentOpenBalance, false)]) );
		},
	},
	methods: {
		handleAddBalance() {
			this.showRechargeSheet = true;
		},
		closeRechargeSheet(shouldRefresh) {
			this.showRechargeSheet = false;
			if (shouldRefresh && this.$refs['balance-table']) {
				this.$refs['balance-table'].fetchData();
			}
		},
		toggleExportType() {
			this.$refs['balance-table'].toggleExportType();
		},
		exportChanges(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let today = new Date().toLocaleString();
			let fileName = businessNameNorm+"_"+this.getString('balance_changes').toLowerCase()+"_"+today.replace(", ", "_");
			exportToFile({
				columns: [
					{ header: this.getString('date'), value: c => formatDateTimeWithSeconds(c.date, false) },
					{ header: this.getString('balance_pre_change') + " " + sym, value: c => this.currencyStore.dbToActualValue(c.balance_pre_change), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('variation') + " " + sym, value: c => this.currencyStore.dbToActualValue(c.amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('balance_post_change') + " " + sym, value: c => this.currencyStore.dbToActualValue(c.balance_pre_change + c.amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('reason'), key: 'reason' },
				],
				rows: this.changes,
				fileName,
				sheetName: this.getString("balance_changes"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					if(this.$refs['balance-table']) {
						this.$refs['balance-table'].fetchData();
					}
				}
			},
		},
	}
}
</script>

<style lang="scss" scoped>
.header-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}

.report-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;

	.warning-limit {
		margin-top: 10px;
		margin-bottom: 10px;
		font-weight: 700;
		color: var(--color-danger);
	}
}
</style>